import React, { useState, useEffect } from 'react';
import axios from 'axios';
import PanelBreadcrumbs from './PanelBreadcrumbs';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const Merchants = ({ setActivePage }) => {
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [merchantDetailsModal, setMerchantDetailsModal] = useState({
    isOpen: false,
    loading: false,
    error: '',
    data: null,
  });
  const [editMerchantModal, setEditMerchantModal] = useState({
    isOpen: false,
    loading: false,
    submitting: false,
    error: '',
    merchantId: null,
    form: {
      shop_name: '',
      shop_url: '',
      email: '',
      name: '',
      phone: '',
      status: 'active',
      commission_rate: '',
      bank_details: {
        account: '',
        ifsc: '',
        holder: '',
      },
    },
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchMerchants();
    }, search ? 500 : 0); // Debounce search

    return () => clearTimeout(timer);
  }, [page, limit, status, search]);

  const fetchMerchants = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get auth token from localStorage
      const authData = localStorage.getItem('superadmin_auth');
      const token = authData ? JSON.parse(authData).token : null;
      
      // Build query parameters
      const params = {
        page,
        limit,
      };
      
      if (status) params.status = status;
      if (search) params.search = search;
      
      const response = await axios.get(`${API_BASE_URL}/api/superadmin/merchants`, {
        params,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Handle response - adjust based on actual API response structure
      const data = response.data;
      
      if (data.merchants && Array.isArray(data.merchants)) {
        setMerchants(data.merchants);
      } else if (Array.isArray(data)) {
        setMerchants(data);
      } else if (data.data && Array.isArray(data.data)) {
        setMerchants(data.data);
      }
      
      // Handle pagination metadata - check multiple possible formats
      if (data.pagination) {
        setTotalPages(data.pagination.total_pages || data.pagination.totalPages || 1);
        setTotalItems(data.pagination.total || data.pagination.total_items || data.pagination.totalItems || 0);
      } else {
        if (data.total_pages !== undefined) {
          setTotalPages(data.total_pages);
        } else if (data.totalPages !== undefined) {
          setTotalPages(data.totalPages);
        } else if (data.total !== undefined && limit) {
          setTotalPages(Math.ceil(data.total / limit));
        } else {
          setTotalPages(1);
        }
        
        if (data.total !== undefined) {
          setTotalItems(data.total);
        } else if (data.total_items !== undefined) {
          setTotalItems(data.total_items);
        } else if (data.totalItems !== undefined) {
          setTotalItems(data.totalItems);
        } else {
          // If no total provided, estimate from current page data
          const currentItems = data.merchants?.length || data.data?.length || (Array.isArray(data) ? data.length : 0);
          setTotalItems(currentItems > 0 ? currentItems : 0);
        }
      }
    } catch (err) {
      // Don't show error if session expired (401) - user will be redirected to login
      if (err.isSessionExpired || (err.response && err.response.status === 401)) {
        return;
      }
      console.error('Error fetching merchants:', err);
      setError(err.response?.data?.error || 'Failed to load merchants');
      setMerchants([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase() || '';
    const colors = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800',
    };
    return colors[statusLower] || 'bg-gray-100 text-gray-800';
  };

  const formatCurrency = (amount) => {
    if (typeof amount === 'number') {
      return '$' + amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return amount || '$0.00';
  };

  const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDetailValue = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.length ? value.join(', ') : '-';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const formatBankDetails = (bankDetails) => {
    if (!bankDetails || typeof bankDetails !== 'object') return '-';
    const account = bankDetails.account ? `A/C: ${bankDetails.account}` : null;
    const ifsc = bankDetails.ifsc ? `IFSC: ${bankDetails.ifsc}` : null;
    const holder = bankDetails.holder ? `Holder: ${bankDetails.holder}` : null;
    const parts = [account, ifsc, holder].filter(Boolean);
    return parts.length ? parts.join(' | ') : '-';
  };

  const normalizeMerchantForEdit = (merchant = {}) => {
    const bankDetails = merchant.bank_details && typeof merchant.bank_details === 'object'
      ? merchant.bank_details
      : {};

    return {
      shop_name: merchant.shop_name || merchant.merchant_shop_name || '',
      shop_url: merchant.shop_url || '',
      email: merchant.email || '',
      name: merchant.name || merchant.merchant_name || '',
      phone: merchant.phone || merchant.mobile || merchant.contact_number || '',
      status: (merchant.status || 'active').toLowerCase(),
      commission_rate: merchant.commission_rate !== undefined && merchant.commission_rate !== null
        ? String(merchant.commission_rate)
        : '',
      bank_details: {
        account: bankDetails.account || '',
        ifsc: bankDetails.ifsc || '',
        holder: bankDetails.holder || '',
      },
    };
  };

  const getAuthToken = () => {
    const authData = localStorage.getItem('superadmin_auth');
    return authData ? JSON.parse(authData).token : null;
  };

  const fetchMerchantById = async (merchantId) => {
    const token = getAuthToken();
    const response = await axios.get(`${API_BASE_URL}/api/superadmin/merchants/${merchantId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data?.data || response.data?.merchant || response.data;
  };

  const closeMerchantDetailsModal = () => {
    setMerchantDetailsModal({
      isOpen: false,
      loading: false,
      error: '',
      data: null,
    });
  };

  const openMerchantDetailsModal = async (merchant) => {
    const merchantIdValue = merchant?.id || merchant?.merchant_id;
    const parsedMerchantId = parseInt(merchantIdValue, 10);
    if (!Number.isInteger(parsedMerchantId) || parsedMerchantId <= 0) {
      setMerchantDetailsModal({
        isOpen: true,
        loading: false,
        error: 'Invalid merchant ID.',
        data: null,
      });
      return;
    }

    setMerchantDetailsModal({
      isOpen: true,
      loading: true,
      error: '',
      data: null,
    });

    try {
      const merchantData = await fetchMerchantById(parsedMerchantId);
      setMerchantDetailsModal({
        isOpen: true,
        loading: false,
        error: '',
        data: merchantData,
      });
    } catch (err) {
      if (err.isSessionExpired || (err.response && err.response.status === 401)) {
        return;
      }
      setMerchantDetailsModal({
        isOpen: true,
        loading: false,
        error: err.response?.data?.message || err.response?.data?.error || 'Failed to load merchant details',
        data: null,
      });
    }
  };

  const closeEditMerchantModal = () => {
    if (editMerchantModal.submitting) return;
    setEditMerchantModal((prev) => ({
      ...prev,
      isOpen: false,
      loading: false,
      submitting: false,
      error: '',
      merchantId: null,
    }));
  };

  const openEditMerchantModal = async (merchant) => {
    const merchantIdValue = merchant?.id || merchant?.merchant_id;
    const parsedMerchantId = parseInt(merchantIdValue, 10);
    if (!Number.isInteger(parsedMerchantId) || parsedMerchantId <= 0) {
      setEditMerchantModal((prev) => ({
        ...prev,
        isOpen: true,
        loading: false,
        submitting: false,
        error: 'Invalid merchant ID.',
        merchantId: null,
      }));
      return;
    }

    setEditMerchantModal((prev) => ({
      ...prev,
      isOpen: true,
      loading: true,
      submitting: false,
      error: '',
      merchantId: parsedMerchantId,
    }));

    try {
      const merchantData = await fetchMerchantById(parsedMerchantId);
      setEditMerchantModal((prev) => ({
        ...prev,
        isOpen: true,
        loading: false,
        submitting: false,
        error: '',
        merchantId: parsedMerchantId,
        form: normalizeMerchantForEdit(merchantData),
      }));
    } catch (err) {
      if (err.isSessionExpired || (err.response && err.response.status === 401)) {
        return;
      }
      setEditMerchantModal((prev) => ({
        ...prev,
        isOpen: true,
        loading: false,
        submitting: false,
        error: err.response?.data?.message || err.response?.data?.error || 'Failed to load merchant details for edit',
        merchantId: parsedMerchantId,
      }));
    }
  };

  const handleEditMerchantFieldChange = (field, value) => {
    setEditMerchantModal((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        [field]: value,
      },
    }));
  };

  const handleEditBankFieldChange = (field, value) => {
    setEditMerchantModal((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        bank_details: {
          ...prev.form.bank_details,
          [field]: value,
        },
      },
    }));
  };

  const submitEditMerchant = async (e) => {
    e.preventDefault();
    if (!editMerchantModal.merchantId) return;

    const normalizedRate = editMerchantModal.form.commission_rate === ''
      ? null
      : Number(editMerchantModal.form.commission_rate);
    if (normalizedRate !== null && !Number.isFinite(normalizedRate)) {
      setEditMerchantModal((prev) => ({
        ...prev,
        error: 'Commission rate must be a valid number.',
      }));
      return;
    }

    const payload = {
      shop_name: editMerchantModal.form.shop_name.trim(),
      shop_url: editMerchantModal.form.shop_url.trim(),
      email: editMerchantModal.form.email.trim(),
      name: editMerchantModal.form.name.trim(),
      phone: editMerchantModal.form.phone.trim(),
      status: editMerchantModal.form.status || 'active',
      commission_rate: normalizedRate,
      bank_details: {
        account: editMerchantModal.form.bank_details.account.trim(),
        ifsc: editMerchantModal.form.bank_details.ifsc.trim(),
        holder: editMerchantModal.form.bank_details.holder.trim(),
      },
    };

    try {
      setEditMerchantModal((prev) => ({
        ...prev,
        submitting: true,
        error: '',
      }));

      const token = getAuthToken();
      await axios.patch(`${API_BASE_URL}/api/superadmin/merchants/${editMerchantModal.merchantId}`, payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      closeEditMerchantModal();
      fetchMerchants();
    } catch (err) {
      if (err.isSessionExpired || (err.response && err.response.status === 401)) {
        return;
      }
      setEditMerchantModal((prev) => ({
        ...prev,
        submitting: false,
        error: err.response?.data?.message || err.response?.data?.error || 'Failed to update merchant',
      }));
    } finally {
      setEditMerchantModal((prev) => ({
        ...prev,
        submitting: false,
      }));
    }
  };

  const handleFilterChange = (filterType, value) => {
    // Reset to page 1 when filters change
    setPage(1);
    if (filterType === 'status') setStatus(value);
    if (filterType === 'search') setSearch(value);
  };

  const clearFilters = () => {
    setPage(1);
    setStatus('');
    setSearch('');
  };

  if (loading && merchants.length === 0) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
            <p className="text-gray-600">Loading merchants...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <PanelBreadcrumbs current="Merchants" setActivePage={setActivePage} />
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Merchants</h1>
          <p className="text-gray-600 mt-1">Manage merchant accounts</p>
        </div>
        <button className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium">
          + Add Merchant
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              type="text"
              value={search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder="Search in shop name, email, or merchant name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending">Pending</option>
            </select>
          </div>
          
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-red-700">{error}</span>
            <button
              onClick={fetchMerchants}
              className="ml-auto px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Merchants Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Merchant</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Products</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {merchants.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                    No merchants found
                  </td>
                </tr>
              ) : (
                merchants.map((merchant, index) => (
                  <tr key={merchant.id || merchant.merchant_id || index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {merchant.shop_name || merchant.name || merchant.merchant_name || '-'}
                      </div>
                      {merchant.name && merchant.shop_name && merchant.name !== merchant.shop_name && (
                        <div className="text-xs text-gray-500 mt-1">{merchant.name}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {merchant.email || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {merchant.product_count || merchant.products_count || merchant.products || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(merchant.total_revenue || merchant.revenue || merchant.total_revenue_amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(merchant.status)}`}>
                        {merchant.status ? (merchant.status.charAt(0).toUpperCase() + merchant.status.slice(1)) : '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        type="button"
                        onClick={() => openMerchantDetailsModal(merchant)}
                        className="text-indigo-600 hover:text-indigo-900 mr-3"
                      >
                        View
                      </button>
                      <button
                        type="button"
                        onClick={() => openEditMerchantModal(merchant)}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing page {page} of {totalPages || 1} {totalItems > 0 && `(${totalItems} total merchants)`}
            {merchants.length > 0 && ` - Showing ${merchants.length} items`}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-4 py-2 text-sm text-gray-700">
              Page {page} / {totalPages || 1}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages || 1, p + 1))}
              disabled={page >= (totalPages || 1) || loading}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>

        {/* Items per page selector */}
        <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Items per page:
          </div>
          <select
            value={limit}
            onChange={(e) => {
              setLimit(parseInt(e.target.value));
              setPage(1);
            }}
            className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
      </div>

      {merchantDetailsModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-2xl rounded-xl bg-white shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Merchant Details</h2>
              <button
                onClick={closeMerchantDetailsModal}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Close merchant details modal"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6">
              {merchantDetailsModal.loading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="flex flex-col items-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mb-3"></div>
                    <p className="text-gray-600">Loading merchant details...</p>
                  </div>
                </div>
              ) : merchantDetailsModal.error ? (
                <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
                  {merchantDetailsModal.error}
                </div>
              ) : merchantDetailsModal.data ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                    <div className="text-xs text-gray-500 uppercase">Merchant ID</div>
                    <div className="text-sm font-semibold text-gray-900 mt-1">{formatDetailValue(merchantDetailsModal.data.id)}</div>
                  </div>
                  <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                    <div className="text-xs text-gray-500 uppercase">User ID</div>
                    <div className="text-sm font-semibold text-gray-900 mt-1">{formatDetailValue(merchantDetailsModal.data.user_id)}</div>
                  </div>
                  <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                    <div className="text-xs text-gray-500 uppercase">Shop Name</div>
                    <div className="text-sm font-semibold text-gray-900 mt-1">
                      {formatDetailValue(merchantDetailsModal.data.shop_name || merchantDetailsModal.data.merchant_shop_name)}
                    </div>
                  </div>
                  <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                    <div className="text-xs text-gray-500 uppercase">Shop URL</div>
                    <div className="text-sm font-semibold text-gray-900 mt-1">{formatDetailValue(merchantDetailsModal.data.shop_url)}</div>
                  </div>
                  <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                    <div className="text-xs text-gray-500 uppercase">Merchant Name</div>
                    <div className="text-sm font-semibold text-gray-900 mt-1">{formatDetailValue(merchantDetailsModal.data.name || merchantDetailsModal.data.merchant_name)}</div>
                  </div>
                  <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                    <div className="text-xs text-gray-500 uppercase">Email</div>
                    <div className="text-sm font-semibold text-gray-900 mt-1">{formatDetailValue(merchantDetailsModal.data.email)}</div>
                  </div>
                  <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                    <div className="text-xs text-gray-500 uppercase">Phone</div>
                    <div className="text-sm font-semibold text-gray-900 mt-1">
                      {formatDetailValue(merchantDetailsModal.data.phone || merchantDetailsModal.data.mobile || merchantDetailsModal.data.contact_number)}
                    </div>
                  </div>
                  <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                    <div className="text-xs text-gray-500 uppercase">Status</div>
                    <div className="text-sm font-semibold text-gray-900 mt-1">{formatDetailValue(merchantDetailsModal.data.status)}</div>
                  </div>
                  <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                    <div className="text-xs text-gray-500 uppercase">Commission Rate</div>
                    <div className="text-sm font-semibold text-gray-900 mt-1">{formatDetailValue(merchantDetailsModal.data.commission_rate)}</div>
                  </div>
                  <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 md:col-span-2">
                    <div className="text-xs text-gray-500 uppercase">Bank Details</div>
                    <div className="text-sm font-semibold text-gray-900 mt-1">{formatBankDetails(merchantDetailsModal.data.bank_details)}</div>
                  </div>
                  <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                    <div className="text-xs text-gray-500 uppercase">Created At</div>
                    <div className="text-sm font-semibold text-gray-900 mt-1">{formatDateTime(merchantDetailsModal.data.created_at)}</div>
                  </div>
                  <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                    <div className="text-xs text-gray-500 uppercase">Updated At</div>
                    <div className="text-sm font-semibold text-gray-900 mt-1">{formatDateTime(merchantDetailsModal.data.updated_at)}</div>
                  </div>
                </div>
              ) : (
                <div className="text-gray-600">No merchant details available.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {editMerchantModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-3xl rounded-xl bg-white shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Edit Merchant</h2>
              <button
                type="button"
                onClick={closeEditMerchantModal}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Close edit merchant modal"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6">
              {editMerchantModal.loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
                </div>
              ) : (
                <form onSubmit={submitEditMerchant} className="space-y-4">
                  {editMerchantModal.error && (
                    <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                      {editMerchantModal.error}
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Shop Name</label>
                      <input
                        type="text"
                        value={editMerchantModal.form.shop_name}
                        onChange={(e) => handleEditMerchantFieldChange('shop_name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Shop URL</label>
                      <input
                        type="text"
                        value={editMerchantModal.form.shop_url}
                        onChange={(e) => handleEditMerchantFieldChange('shop_url', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                      <input
                        type="email"
                        value={editMerchantModal.form.email}
                        onChange={(e) => handleEditMerchantFieldChange('email', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                      <input
                        type="text"
                        value={editMerchantModal.form.name}
                        onChange={(e) => handleEditMerchantFieldChange('name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                      <input
                        type="text"
                        value={editMerchantModal.form.phone}
                        onChange={(e) => handleEditMerchantFieldChange('phone', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                      <select
                        value={editMerchantModal.form.status}
                        onChange={(e) => handleEditMerchantFieldChange('status', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="pending">Pending</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Commission Rate</label>
                      <input
                        type="number"
                        step="0.01"
                        value={editMerchantModal.form.commission_rate}
                        onChange={(e) => handleEditMerchantFieldChange('commission_rate', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-gray-800 mb-2">Bank Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Account</label>
                        <input
                          type="text"
                          value={editMerchantModal.form.bank_details.account}
                          onChange={(e) => handleEditBankFieldChange('account', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">IFSC</label>
                        <input
                          type="text"
                          value={editMerchantModal.form.bank_details.ifsc}
                          onChange={(e) => handleEditBankFieldChange('ifsc', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Holder</label>
                        <input
                          type="text"
                          value={editMerchantModal.form.bank_details.holder}
                          onChange={(e) => handleEditBankFieldChange('holder', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={closeEditMerchantModal}
                      disabled={editMerchantModal.submitting}
                      className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={editMerchantModal.submitting}
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium disabled:opacity-50"
                    >
                      {editMerchantModal.submitting ? 'Updating...' : 'Update Merchant'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Merchants;
